import { useMemo, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { useApp } from '../store/AppContext'
import { loadCaseNotes, saveCaseNote, visibleCaseNotes, type CaseNoteRecord } from '../lib/caseNotes'

export function CaseNotes() {
  const { currentUser, allUsers } = useApp()
  const [version, setVersion] = useState(0)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [allowed, setAllowed] = useState<string[]>([])
  const canCreate = ['participant', 'rep', 'coordinator'].includes(currentUser.role)

  const notes = useMemo(() => visibleCaseNotes(currentUser.id), [currentUser.id, version])
  const selectable = allUsers.filter((user) => user.id !== currentUser.id)

  const toggleAllowed = (userId: string) => {
    setAllowed((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const create = () => {
    if (!title.trim() || !body.trim() || !canCreate) return
    saveCaseNote({
      id: `case-${Date.now()}`,
      authorId: currentUser.id,
      title: title.trim(),
      body: body.trim(),
      createdAt: new Date().toISOString(),
      allowedUserIds: allowed,
    })
    setTitle('')
    setBody('')
    setAllowed([])
    setVersion((v) => v + 1)
  }

  const changeAccess = (note: CaseNoteRecord, userId: string) => {
    const canManage = note.authorId === currentUser.id || currentUser.role === 'participant' || currentUser.role === 'rep'
    if (!canManage) return
    const next = note.allowedUserIds.includes(userId)
      ? note.allowedUserIds.filter((id) => id !== userId)
      : [...note.allowedUserIds, userId]
    saveCaseNote({ ...note, allowedUserIds: next })
    setVersion((v) => v + 1)
  }

  return (
    <div>
      <TopBar title="Protected Case Notes" subtitle="Separate from the main document library" />
      <div className="flex flex-col gap-4 px-4 py-4">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-800">
          Case notes are private by default. They do not appear in Documents or general document search. Access must be deliberately granted to specific people.
        </div>

        {canCreate && (
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
            <h2 className="text-sm font-bold text-slate-900">New case note</h2>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Note title" className="input mt-3" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write case note..." className="input mt-3 min-h-32" />
            <p className="mt-3 text-xs font-semibold text-slate-600">Who can see this note?</p>
            <div className="mt-2 flex flex-col gap-2">
              {selectable.map((user) => (
                <label key={user.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={allowed.includes(user.id)} onChange={() => toggleAllowed(user.id)} />
                  {user.name} <span className="text-xs text-slate-400">— {user.title ?? user.role.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
            <button onClick={create} className="mt-4 w-full rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">
              Save protected case note
            </button>
          </section>
        )}

        <section>
          <h2 className="mb-2 text-sm font-bold text-slate-900">Case notes I can access</h2>
          <div className="flex flex-col gap-3">
            {notes.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">No case notes shared with you.</div>}
            {notes.map((note) => {
              const author = allUsers.find((user) => user.id === note.authorId)
              const canManage = note.authorId === currentUser.id || currentUser.role === 'participant' || currentUser.role === 'rep'
              return (
                <article key={note.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
                  <h3 className="text-sm font-bold text-slate-900">{note.title}</h3>
                  <p className="mt-1 text-xs text-slate-400">{author?.name} · {new Date(note.createdAt).toLocaleString()}</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">{note.body}</p>
                  <p className="mt-4 text-xs font-semibold text-slate-600">Access</p>
                  <div className="mt-2 flex flex-col gap-1.5">
                    {selectable.map((user) => (
                      <label key={user.id} className="flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          disabled={!canManage}
                          checked={note.allowedUserIds.includes(user.id)}
                          onChange={() => changeAccess(note, user.id)}
                        />
                        {user.name}
                      </label>
                    ))}
                  </div>
                </article>
              )
            })}
          </div>
        </section>
      </div>
    </div>
  )
}

